import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Tag } from '../types';
import ApiService from '../services/apiService';
import { logger } from '../utils/logger';

interface TagsManagerProps {
    fileId: string;
    currentTags: Tag[];
    onTagsChange: (tags: Tag[]) => void;
    className?: string;
    children?: React.ReactNode;
}

const TagsManager: React.FC<TagsManagerProps> = ({
    fileId,
    currentTags,
    onTagsChange,
    className = '',
    children
}) => {
    const { t } = useTranslation();
    const [allTags, setAllTags] = useState<Tag[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [newTagColor, setNewTagColor] = useState('#3B82F6');
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const apiService = new ApiService();

    // Predefined colors for tags
    const tagColors = [
        '#3B82F6', // Blue
        '#EF4444', // Red  
        '#10B981', // Green
        '#F59E0B', // Yellow
        '#8B5CF6', // Purple
        '#EC4899', // Pink
        '#6B7280', // Gray
        '#F97316', // Orange
        '#14B8A6', // Teal
        '#84CC16', // Lime
    ];

    useEffect(() => {
        loadTags();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- eenmalig laden bij mount
    }, []);

    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const updatePosition = () => {
                if (buttonRef.current) {
                    const rect = buttonRef.current.getBoundingClientRect();
                    setDropdownPosition({
                        top: rect.bottom,
                        left: rect.left
                    });
                }
            };

            updatePosition();
            window.addEventListener('resize', updatePosition);
            window.addEventListener('scroll', updatePosition, true);

            return () => {
                window.removeEventListener('resize', updatePosition);
                window.removeEventListener('scroll', updatePosition, true);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setShowCreateForm(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const loadTags = async () => {
        try {
            setIsLoading(true);
            const tags = await apiService.getTags();
            setAllTags(tags);
        } catch (error) {
            logger.error('Error loading tags:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddTag = async (tag: Tag) => {
        try {
            const success = await apiService.addTagsToRecording(fileId, [tag.id]);
            if (success) {
                const updatedTags = [...currentTags, tag];
                onTagsChange(updatedTags);
            }
        } catch (error) {
            logger.error('Error adding tag:', error);
        }
    };

    const handleRemoveTag = async (tagId: number) => {
        try {
            const success = await apiService.removeTagFromRecording(fileId, tagId);
            if (success) {
                const updatedTags = currentTags.filter(t => t.id !== tagId);
                onTagsChange(updatedTags);
            }
        } catch (error) {
            logger.error('Error removing tag:', error);
        }
    };

    const handleCreateTag = async () => {
        if (!newTagName.trim()) return;

        try {
            const newTag = await apiService.createTag(newTagName.trim(), '', newTagColor);
            setAllTags(prev => [...prev, newTag]);
            await handleAddTag(newTag);
            setNewTagName('');
            setShowCreateForm(false);
            setSearchTerm('');
        } catch (error) {
            logger.error('Error creating tag:', error);
        }
    };

    const getFilteredTags = () => {
        const currentTagIds = currentTags.map(t => t.id);
        return allTags
            .filter(tag => !currentTagIds.includes(tag.id))
            .filter(tag =>
                searchTerm === '' ||
                tag.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
    };

    const getTagDisplayName = (tagName: string) => {
        return tagName.length > 20 ? tagName.substring(0, 20) + '...' : tagName;
    };

    return (
        <div className={`relative ${className}`}>
            {/* Current Tags Display */}
            <div className="flex flex-wrap gap-2 mb-2">
                {children}
                {currentTags.map((tag) => (
                    <div
                        key={tag.id}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white"
                        style={{ backgroundColor: tag.color }}
                    >
                        <span>{getTagDisplayName(tag.name)}</span>
                        <button
                            onClick={() => handleRemoveTag(tag.id)}
                            className="ml-1 hover:bg-black hover:bg-opacity-20 rounded-full p-0.5 transition-colors"
                        >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                ))}

                {/* Add Tag Button */}
                <button
                    ref={buttonRef}
                    onClick={() => setIsOpen(!isOpen)}
                    className="inline-flex items-center px-2 py-1 border border-gray-300 rounded-full text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 transition-colors"
                >
                    <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    {t('tagsManager.addTag')}
                </button>
            </div>

            {/* Dropdown Portal */}
            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed w-72 bg-white rounded-lg shadow-lg border border-gray-200 mt-1"
                    style={{
                        top: `${dropdownPosition.top}px`,
                        left: `${dropdownPosition.left}px`,
                        zIndex: 9999
                    }}
                >
                    <div className="p-3">
                        {/* Search input */}
                        <input
                            type="text"
                            placeholder={t('tagsManager.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-primary-500"
                            autoFocus
                        />
                    </div>

                    <div className="max-h-48 overflow-y-auto">
                        {/* Available tags */}
                        {getFilteredTags().length > 0 && (
                            <div className="px-3 pb-2">
                                <div className="text-xs font-medium text-gray-500 mb-2">{t('tagsManager.availableTags')}</div>
                                <div className="space-y-1">
                                    {getFilteredTags().map((tag) => (
                                        <button
                                            key={tag.id}
                                            onClick={() => handleAddTag(tag)}
                                            className="w-full flex items-center px-2 py-2 text-left hover:bg-gray-50 rounded-md transition-colors"
                                        >
                                            <div
                                                className="w-3 h-3 rounded-full mr-2"
                                                style={{ backgroundColor: tag.color }}
                                            />
                                            <span className="text-sm text-gray-900">{tag.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Create new tag */}
                        {searchTerm && !allTags.some(tag => tag.name.toLowerCase() === searchTerm.toLowerCase()) && (
                            <div className="border-t border-gray-200 px-3 py-2">
                                {!showCreateForm ? (
                                    <button
                                        onClick={() => {
                                            setShowCreateForm(true);
                                            setNewTagName(searchTerm);
                                        }}
                                        className="w-full flex items-center px-2 py-2 text-left hover:bg-gray-50 rounded-md transition-colors"
                                    >
                                        <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-sm text-gray-600">{t('tagsManager.createNewTag', { name: searchTerm })}</span>
                                    </button>
                                ) : (
                                    <div className="space-y-2">
                                        <input
                                            type="text"
                                            placeholder={t('tagsManager.tagNamePlaceholder')}
                                            value={newTagName}
                                            onChange={(e) => setNewTagName(e.target.value)}
                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                        />
                                        <div className="flex items-center space-x-2">
                                            <div className="flex space-x-1">
                                                {tagColors.slice(0, 5).map((color) => (
                                                    <button
                                                        key={color}
                                                        onClick={() => setNewTagColor(color)}
                                                        className={`w-4 h-4 rounded-full border-2 ${newTagColor === color ? 'border-gray-400' : 'border-gray-200'}`}
                                                        style={{ backgroundColor: color }}
                                                    />
                                                ))}
                                            </div>
                                            <div className="flex space-x-1">
                                                <button
                                                    onClick={handleCreateTag}
                                                    className="px-2 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700"
                                                >
                                                    {t('tagsManager.create')}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setShowCreateForm(false);
                                                        setNewTagName('');
                                                    }}
                                                    className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                                                >
                                                    {t('common.cancel')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {getFilteredTags().length === 0 && !searchTerm && (
                            <div className="px-3 py-4 text-center text-sm text-gray-500">
                                {t('tagsManager.noAvailableTags')}
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TagsManager;